import React, { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  deleteCustomerIndex,
  deleteLastAdded,
} from '../../../Appointment/store/Actions'

import { useHistory, useParams } from '../../../Router'

import { BackButtonAction, BackButtonLink, BackButtonText } from './style'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

type Props = {
  customPath?: string
  goBack?: number
  isAppointment?: boolean
  rootPathCheck?: boolean
  extraMargin?: boolean
}

/**
 * OB-499: Click targets are native <button> / <a> instead of TouchableOpacity
 * and react-router-dom <Link>. The previous mix went through both react-
 * router's synthetic click handler and react-native-web's touch system,
 * which on some Android OEM browsers (OB-494) misresolved post-scroll
 * clicks against the wrong element.
 *
 * Behavior is preserved across all four branches:
 *  1. rootPathCheck + activities -> programmatic deleteCustomerIndex + push
 *  2. rootPathCheck + no activities -> link to relative "appointment" path
 *  3. customPath -> link to that path
 *  4. default -> programmatic history.go / goBack
 */
export const BackButton = ({
  customPath,
  goBack,
  isAppointment,
  rootPathCheck = false,
  extraMargin = false,
}: Props) => {
  const { establishmentId } = useParams<{ [key: string]: string }>()
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { settings, appointment } = useAppSelector((state) => ({
    appointment: state.appointment,
    settings: state.settings.values,
  }))

  const { currentCustomerIndex } = appointment
  const hasActivities: boolean =
    appointment.customers.reduce(
      (previousValue, currentValue) =>
        previousValue + currentValue.activities.length,
      0
    ) > 0

  const navigateOnClick = (
    e: MouseEvent<HTMLAnchorElement>,
    target: string
  ) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey
    ) {
      return
    }
    e.preventDefault()
    history.push(target)
  }

  if (!hasActivities && rootPathCheck && !customPath) {
    return null
  }

  if (hasActivities && rootPathCheck) {
    if (
      appointment.customers[currentCustomerIndex] ||
      appointment.customers[currentCustomerIndex].activities.length !== 0
    ) {
      return (
        <BackButtonAction
          type="button"
          onClick={() => {
            dispatch(deleteCustomerIndex(currentCustomerIndex))
            history.push(`/${establishmentId}/appointment`)
          }}
        >
          <BackButtonText
            $extraMargin={extraMargin}
            color={settings.highlightColor}
          >
            {t('Back')}
          </BackButtonText>
        </BackButtonAction>
      )
    }

    // Original used react-router v5 <Link to="appointment"> which resolves
    // relative to the matched Route. From the typical /:establishmentId/...
    // mounting that lands on /:establishmentId/appointment.
    const target = `/${establishmentId}/appointment`
    return (
      <BackButtonLink
        href={target}
        onClick={(e) => navigateOnClick(e, target)}
      >
        <BackButtonText
          $extraMargin={extraMargin}
          color={settings.highlightColor}
        >
          {t('Back')}
        </BackButtonText>
      </BackButtonLink>
    )
  }

  if (typeof customPath !== 'undefined') {
    return (
      <BackButtonLink
        href={customPath}
        onClick={(e) => navigateOnClick(e, customPath)}
      >
        <BackButtonText
          $extraMargin={extraMargin}
          color={settings.highlightColor}
        >
          {t('Back')}
        </BackButtonText>
      </BackButtonLink>
    )
  }

  const goBackTwoSteps = (): boolean => {
    const { customers } = appointment
    const currentCustomer = customers[appointment.currentCustomerIndex]
    const { activities } = currentCustomer

    if (activities.length > 0) {
      const lastActivity = activities[activities.length - 1]
      const listStructurePersonSelect =
        lastActivity.isPersonSelectable && settings.isListView
      // Go back 2 steps if:
      // 1. Activity has a description (TileStructure modal flow)
      // 2. OR activity has person selection enabled and is in list view
      return !!lastActivity.description || listStructurePersonSelect
    }
    return false
  }

  const numberOfSteps: number = goBack || -1

  const goBackLogic = () => {
    if (isAppointment) {
      dispatch(deleteLastAdded())
      history.go(numberOfSteps)
    } else if (goBackTwoSteps()) {
      history.go(-2)
    } else {
      history.goBack()
    }
  }

  return (
    <BackButtonAction type="button" onClick={goBackLogic}>
      <BackButtonText
        $extraMargin={extraMargin}
        color={settings.highlightColor}
      >
        {t('Back')}
      </BackButtonText>
    </BackButtonAction>
  )
}
